/**
 * Contacts' online status ("online" / "last seen …"), as reported by the relay
 * for the users this app watches — see `messaging.watchPresence`.
 */
import { create } from "zustand";

export interface PeerPresence {
  online: boolean;
  lastSeen: number | null;
}

interface PresenceState {
  peers: Record<string, PeerPresence>;
  set(userId: string, p: PeerPresence): void;
  clear(): void;
}

export const usePresenceStore = create<PresenceState>((set) => ({
  peers: {},
  set: (userId, p) => set((s) => ({ peers: { ...s.peers, [userId]: p } })),
  clear: () => set({ peers: {} }),
}));

export function usePresence(userId: string | undefined): PeerPresence | undefined {
  return usePresenceStore((s) => (userId ? s.peers[userId] : undefined));
}

/** "online", "last seen today at 14:05", "last seen yesterday at 09:12", "last seen 12 Sep at 18:40". */
export function describePresence(p: PeerPresence | undefined, now: number = Date.now()): string | null {
  if (!p) return null;
  if (p.online) return "online";
  if (p.lastSeen === null) return null;
  const seen = new Date(p.lastSeen);
  const time = seen.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const dayMs = 24 * 60 * 60 * 1000;
  if (p.lastSeen >= startOfToday.getTime()) return `last seen today at ${time}`;
  if (p.lastSeen >= startOfToday.getTime() - dayMs) return `last seen yesterday at ${time}`;
  const date = seen.toLocaleDateString([], { day: "numeric", month: "short" });
  return `last seen ${date} at ${time}`;
}
